import { z } from 'zod';
import { itemDefinitionIdSchema } from '../inventory/definitions';
import {
  SKIN_BANNER_CURRENCY,
  SKIN_BANNER_STATUSES,
  SKIN_DUPLICATE_POLICIES,
} from '../skins/banner';
import { SKIN_SOCKET_MAX, SKIN_SOCKET_MIN } from '../skins/sockets';
import { RELEASE_STAGES } from '../store/release';

/**
 * The Cash skin roulette on the wire (ADR 0040).
 *
 * ## Two routes, both session-protected
 *
 * `GET /api/skins/gacha/banner` publishes everything a player needs BEFORE
 * spending anything. `POST /api/skins/gacha/roll` is the only route in the
 * product that creates a skin, and it charges for it.
 *
 * ## The odds are public before the charge
 *
 * The banner carries the price, the currency, the pool with a chance per skin,
 * the chance of each socket count from 1 to 6, the version, the period, the
 * duplicate policy and the fact that the result is soulbound. None of it is
 * hidden and none of it is behind the purchase.
 *
 * ## What a request may say, and what it cannot
 *
 * A banner id, the version and fingerprint the player was SHOWN, and a replay
 * key. **Never an account id** - there is no field for it, so "one account
 * cannot act as another" is not a check that could be forgotten. Never a
 * definition, never a socket count, never a price, never a seed and never one
 * bit of entropy. There is no field in which a client could ask for an outcome.
 *
 * ## What an answer carries, and what it never carries
 *
 * The skin, the sockets, what was charged, the resulting balance and an opaque
 * receipt. **No nonce, no seed, no entropy and no internal row.** A record a
 * future draw could be predicted from is not transparency, it is a leak.
 */

export const SKIN_GACHA_BANNER_ROUTE = '/api/skins/gacha/banner' as const;
export const SKIN_GACHA_ROLL_ROUTE = '/api/skins/gacha/roll' as const;

/**
 * Bumped when this response shape changes incompatibly.
 *
 * 2: **the banner states its own terms** (gate V24C). A screen that charges Cash
 * has to say, before the charge, that there is no guarantee by roll count and
 * which release stage's reset and recredit rules apply to what it is selling.
 * Version 1 carried neither, so an interface would have had to compose those
 * sentences itself - and `store/alpha-terms.ts` exists precisely because a
 * promise about a reset must be rendered from one source rather than written
 * again per screen.
 *
 * The bump is not decorative: `pity` and `releaseStage` are REQUIRED, so a
 * version-1 server's answer fails validation in a version-2 client rather than
 * being drawn with the disclosure silently missing, and a version-1 client
 * refuses a version-2 answer at the `contractVersion` literal. No banner has
 * ever been active and no client has ever spoken version 1, so nothing
 * deployed can be caught by either direction.
 */
export const SKIN_GACHA_CONTRACT_VERSION = 2;

/** A Cash amount on the wire: a decimal string, never a JSON number. */
const cashAmountSchema = z.string().regex(/^[0-9]{1,40}$/, 'cash amount must be decimal digits');

/** One skin in the pool, with its published chance. */
export const skinBannerPoolEntryWireSchema = z.object({
  definitionId: itemDefinitionIdSchema,
  displayName: z.string().min(1),
  /** One sentence saying what it is. Null when the content has none yet. */
  blurb: z.string().min(1).nullable(),
  /** The integer weight the draw actually uses. Published, not paraphrased. */
  weight: z.number().int().positive(),
  /** The same thing as a percentage, in basis points, rounded DOWN. */
  chanceBps: z.number().int().min(0).max(10_000),
});

/** One socket count and how likely it is. Six of these, always. */
export const skinSocketOddsWireSchema = z.object({
  sockets: z.number().int().min(SKIN_SOCKET_MIN).max(SKIN_SOCKET_MAX),
  weight: z.number().int().positive(),
  chanceBps: z.number().int().positive(),
  /** `"25,00%"`. Display only; the draw uses `weight`. */
  label: z.string().min(1),
});

export const skinGachaBannerResponseSchema = z.object({
  contractVersion: z.literal(SKIN_GACHA_CONTRACT_VERSION),
  bannerId: z.string().min(1).max(64),
  bannerVersion: z.number().int().positive(),
  status: z.enum(SKIN_BANNER_STATUSES),
  /** ISO-8601 UTC, or null when there is no scheduled boundary. */
  startsAt: z.iso.datetime().nullable(),
  endsAt: z.iso.datetime().nullable(),
  priceCash: cashAmountSchema,
  /** Always Cash. A literal, so a Gold price cannot be expressed at all. */
  currency: z.literal(SKIN_BANNER_CURRENCY),
  pool: z.array(skinBannerPoolEntryWireSchema).min(1),
  socketProfileId: z.string().min(1).max(64),
  socketProfileVersion: z.number().int().positive(),
  /** All six, always, so nothing about the spread is inferred by the client. */
  socketOdds: z.array(skinSocketOddsWireSchema).length(6),
  duplicatePolicy: z.enum(SKIN_DUPLICATE_POLICIES),
  /**
   * The hash of the configuration above.
   *
   * A roll must carry it back. If the banner has changed in the meantime the
   * server refuses rather than charging the new price or drawing from the new
   * pool.
   */
  fingerprint: z.string().regex(/^[0-9a-f]{64}$/, 'fingerprint must be lower-case SHA-256 hex'),
  /** Stated on the banner, not discovered after the purchase. */
  soulbound: z.literal(true),
  /** Whether sockets do anything in a duel. They do not, and it is published. */
  affectsPvp: z.literal(false),
  /**
   * The guarantee this banner offers after N rolls. **There is none.**
   *
   * `null` is the ANSWER, not a placeholder for a value the server forgot: the
   * closed alpha ships without pity, and every roll is independent of every
   * roll before it. A field that says so explicitly is what lets an interface
   * publish "não há garantia por quantidade de rolagens" as a fact it was TOLD
   * rather than as a fact it inferred from a missing key - and it is what a
   * future guarantee would fill in, so the screen changes with the server.
   *
   * Typed as `null` rather than as an optional object because there is no
   * decided shape for a guarantee. Inventing one here would be deciding what
   * pity is, which nobody has.
   */
  pity: z.null(),
  /**
   * Which release stage the SERVER is in.
   *
   * Not the wording, and deliberately not: `store/alpha-terms.ts` already holds
   * the exact paragraphs each stage promises, both sides import it, and it says
   * in as many words that an interface composes no sentence of its own about a
   * reset or a recredit. What a client cannot know on its own is WHICH stage is
   * live - that is server configuration - so the stage is what travels, and the
   * text is looked up from the one module that owns it.
   */
  releaseStage: z.enum(RELEASE_STAGES),
  /** The caller's own balance, so the panel needs one request rather than two. */
  balance: cashAmountSchema,
});
export type SkinGachaBannerResponse = z.infer<typeof skinGachaBannerResponseSchema>;

export const skinGachaRollRequestSchema = z
  .object({
    bannerId: z.string().min(1).max(64),
    /**
     * The version and fingerprint the player was SHOWN.
     *
     * Both, not either: the version says which configuration, the fingerprint
     * proves it is byte-for-byte the one whose odds were displayed. A banner
     * edited between the odds screen and the button must not silently charge a
     * new price.
     */
    bannerVersion: z.number().int().positive(),
    bannerFingerprint: z.string().regex(/^[0-9a-f]{64}$/),
    /**
     * Replay guard. The SAME key with the same body returns the same skin, the
     * same sockets and one charge; with a different body it is a conflict.
     */
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type SkinGachaRollRequest = z.infer<typeof skinGachaRollRequestSchema>;

export const skinGachaRollResponseSchema = z.object({
  contractVersion: z.literal(SKIN_GACHA_CONTRACT_VERSION),
  /** An opaque id for this roll. Not a row id a client can do anything with. */
  receiptId: z.uuid(),
  bannerId: z.string().min(1).max(64),
  bannerVersion: z.number().int().positive(),
  definitionId: itemDefinitionIdSchema,
  displayName: z.string().min(1),
  itemInstanceId: z.uuid(),
  /** Immutable, drawn once, and never re-rolled. */
  socketCapacity: z.number().int().min(SKIN_SOCKET_MIN).max(SKIN_SOCKET_MAX),
  socketProfileId: z.string().min(1).max(64),
  socketProfileVersion: z.number().int().positive(),
  /** What was charged, and what is left. Both decimal strings. */
  costCash: cashAmountSchema,
  balance: cashAmountSchema,
  currency: z.literal(SKIN_BANNER_CURRENCY),
  soulbound: z.literal(true),
  /** True when the same key had already been applied. A replay is a SUCCESS. */
  replayed: z.boolean(),
  rolledAt: z.iso.datetime(),
});
export type SkinGachaRollResponse = z.infer<typeof skinGachaRollResponseSchema>;
